import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import type { NegotiationMessage } from "@/types/negotiations";

function formatTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface MessageBubbleProps {
  message: NegotiationMessage;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isOutbound = message.direction === "outbound";
  const isDraft = message.status === "draft" || message.status === "pending_approval";

  return (
    <div
      className={cn(
        "flex flex-col gap-1 max-w-[75%]",
        isOutbound ? "ml-auto items-end" : "items-start"
      )}
    >
      {/* Header row */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-1">
        {message.ai_generated && (
          <span className="flex items-center gap-0.5 text-primary">
            <Sparkles className="w-3 h-3" />
            AI
          </span>
        )}
        {isDraft && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 border-warning/40 text-warning"
          >
            draft
          </Badge>
        )}
        <span>{formatTime(message.sent_at ?? message.received_at ?? message.created_at)}</span>
      </div>

      {/* Bubble */}
      <div
        className={cn(
          "rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap",
          isOutbound
            ? "bg-primary/80 text-primary-foreground rounded-tr-sm"
            : "bg-card border border-border text-foreground rounded-tl-sm",
          isDraft && "opacity-70 border border-dashed border-warning/40"
        )}
      >
        {message.body_text ?? "(no content)"}
      </div>

      {/* AI rationale */}
      {message.ai_rationale && (
        <p className="text-[11px] text-muted-foreground/70 italic px-1 max-w-xs">
          {message.ai_rationale}
        </p>
      )}
    </div>
  );
}
